import React, { useState, useRef, useEffect } from 'react';
import { observer, inject } from "mobx-react";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

// Bootstrap's .modal layer is 1050. Staying under it keeps modals that still
// use components/modal.js stacked above this one when nested.
const Z_INDEX = 1040;
// The discard prompt sits above its own dialog but still under .modal.
const PROMPT_Z_INDEX = 1045;

const SIZES = {
    sm : 480,
    md : 640,
    lg : 880
};

// Fallback so a missing translation renders readable text instead of "undefined".
const translate = (data, key, fallback) => {
    const value = data?.t?.read?.(key);

    return typeof value === 'string' || typeof value === 'number' ? value : fallback;
};

let idCounter = 0;

/**
 * MUI Dialog based replacement for components/modal.js.
 *
 * Every prop modal.js accepted still works. On top of that:
 *
 *   variant      'form' (default) · 'confirm' (compact) · 'fullscreen'
 *   size         'sm' | 'md' | 'lg' — ignored when `width` is given
 *   subtitle     one line under the title, wired to aria-describedby
 *   isDirty      dismissing asks "Discard changes?" first
 *   destructive  the ok action reads as destructive (error colour, never autofocused)
 *   dividers     rules between title/content and content/actions
 *   zIndex       raise a nested dialog above its editor
 *   closeDisabled  lock dismissal while the caller's async action is running
 *
 * Callers that own their own async state drive the per-button spinners with
 * `okLoading`, `secondaryLoading` and `secondaryActions[].loading` instead of
 * relying on the internal processing state.
 *
 * `centered` is accepted but ignored: MUI centers the dialog already.
 */
const ModalDialog = inject("data")(observer(({
    data,
    title,
    subtitle,
    titleAction,
    titleText,
    variant = 'form',
    zIndex = Z_INDEX,
    closeDisabled = false,
    size,
    width,
    height,
    overflow,
    dividers = false,
    fullScreenOnMobile,
    showCloseIcon,
    isDirty = false,
    disableBackdropClose,
    disableEscapeClose,
    onClose,
    cancelAction,
    cancelText,
    secondaryAction,
    secondaryText,
    secondaryLoading = false,
    secondaryActions,
    okAction,
    okText,
    okLoading = false,
    okLoadingText,
    okIsSubmit,
    destructive = false,
    invalid,
    noProcessingLoading,
    finishProcessing,
    children
}) => {

    const theme = useTheme();
    const isConfirm = variant === 'confirm';
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // A compact confirmation stays compact on a phone; anything else takes the
    // whole screen rather than squeezing a form into a 340px card.
    const allowMobileFullScreen = fullScreenOnMobile != null ? fullScreenOnMobile : !isConfirm;
    const fullScreen = variant === 'fullscreen' || (isSmallScreen && allowMobileFullScreen);

    // `processing` names the action that is running ('ok', 'secondary', 'title'
    // or a secondaryActions index) so only that button shows a spinner.
    const [processing, setProcessing] = useState(null);
    const [discarding, setDiscarding] = useState(false);
    // The close that runs once the user confirms they want to lose their edits.
    const pendingClose = useRef(null);

    const ids = useRef(null);
    if (ids.current === null) {
        idCounter += 1;
        ids.current = {
            title : `modal-dialog-title-${idCounter}`,
            description : `modal-dialog-description-${idCounter}`
        };
    }

    // Mirrors the class componentDidUpdate: only sync when the prop changes,
    // never on the first render.
    const previousFinishProcessing = useRef(finishProcessing);
    useEffect(() => {
        if (previousFinishProcessing.current !== finishProcessing) {
            previousFinishProcessing.current = finishProcessing;
            // Callers pass a boolean; keep the running action's identity when
            // it is truthy so the right button keeps its spinner.
            setProcessing((current) => (finishProcessing ? (current ?? 'ok') : null));
        }
    }, [finishProcessing]);

    const busy = processing !== null;
    // `noProcessingLoading` callers drive their own feedback — stay out of the way.
    const showsBusyState = busy && !noProcessingLoading;

    const runAction = (key, action) => {
        setProcessing(key);
        let valid = action();

        // An action returning false means it refused the input; give the form back.
        if (valid === false) setProcessing(null);
    };

    const dismiss = () => {
        if (cancelAction) {
            cancelAction();
            return;
        }
        if (onClose) onClose();
    };

    // Every dismissal path — backdrop, escape, close icon, cancel button — goes
    // through here so unsaved changes are guarded consistently.
    const requestClose = (closer) => {
        if (closeDisabled) return;
        if (isDirty) {
            pendingClose.current = closer;
            setDiscarding(true);
            return;
        }
        closer();
    };

    const handleClose = (e, reason) => {
        if (reason === "backdropClick" && disableBackdropClose) return;
        if (reason === "escapeKeyDown" && disableEscapeClose) return;

        requestClose(dismiss);
    };

    const confirmDiscard = () => {
        const closer = pendingClose.current;

        pendingClose.current = null;
        setDiscarding(false);
        if (closer) closer();
    };

    const canDismiss = Boolean(cancelAction || onClose);
    const closeIconVisible = showCloseIcon != null ? showCloseIcon : canDismiss;

    const hasSecondaryActions = Array.isArray(secondaryActions) && secondaryActions.length > 0;
    const showsCancel = cancelAction != null && cancelText != null;
    const rightButtonCount =
        (secondaryAction != null ? 1 : 0) +
        (hasSecondaryActions ? secondaryActions.length : 0) +
        (okAction != null ? 1 : 0) +
        (okIsSubmit ? 1 : 0);
    const hasFooter = showsCancel || rightButtonCount > 0;
    // Three or more buttons on a phone stack instead of shrinking to nothing.
    const stackActions = fullScreen && (rightButtonCount + (showsCancel ? 1 : 0)) > 2;

    // Bootstrap markup throughout the footer so these read exactly as the
    // buttons modal.js rendered; MUI only supplies the dialog shell.
    const spinner = <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />;
    const buttonClass = (base) => `${base}${stackActions ? ' btn-block' : ''}`;

    const paperSx = fullScreen ? {} : {
        width : width || SIZES[size] || SIZES[isConfirm ? 'sm' : 'md'],
        maxWidth : 'calc(100vw - 32px)',
        height : height || 'auto',
        margin : 0
    };

    return <>
        <Dialog
            open
            onClose={handleClose}
            fullScreen={fullScreen}
            maxWidth={false}
            aria-labelledby={title ? ids.current.title : undefined}
            aria-describedby={subtitle ? ids.current.description : undefined}
            sx={{ zIndex, width: '100vw', height: '100dvh' }}
            PaperProps={{ sx: paperSx }}
        >
            {title && <DialogTitle
                id={`${ids.current.title}-heading`}
                sx={{
                    px : 3,
                    pt : 2.5,
                    pb : subtitle ? 1 : 2.5,
                    display : 'flex',
                    alignItems : 'flex-start',
                    justifyContent : 'space-between',
                    gap : 2
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography id={ids.current.title} component="span" variant={isConfirm ? 'h6' : 'h5'} sx={{ display: 'block' }}>
                        {title}
                    </Typography>
                    {subtitle && <Typography
                        id={ids.current.description}
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        {subtitle}
                    </Typography>}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {titleAction != null && <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        disabled={busy}
                        onClick={() => runAction('title', titleAction)}
                    >
                        {processing === 'title' ? spinner : null}
                        {titleText}
                    </button>}

                    {closeIconVisible && <IconButton
                        aria-label={translate(data, 'modal_dialog.close', 'Close')}
                        disabled={closeDisabled}
                        size="small"
                        edge="end"
                        onClick={() => requestClose(dismiss)}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>}
                </Box>
            </DialogTitle>}

            <DialogContent
                dividers={dividers}
                aria-busy={showsBusyState}
                sx={{
                    px : 3,
                    py : title ? 1 : 2.5,
                    maxHeight : (height || fullScreen) ? '100%' : '70vh',
                    position : 'relative',
                    overflow : overflow ? overflow : 'auto'
                }}
            >
                {children}

                {/* The body stays readable underneath; the overlay only blocks input. */}
                {showsBusyState && <Box
                    sx={{
                        position : 'absolute',
                        inset : 0,
                        display : 'flex',
                        alignItems : 'center',
                        justifyContent : 'center',
                        backgroundColor : 'rgba(255, 255, 255, 0.6)',
                        zIndex : 1
                    }}
                >
                    <span className="spinner-border text-primary" role="status">
                        <span className="sr-only">{translate(data, 'please_wait', 'Please wait')}</span>
                    </span>
                </Box>}
            </DialogContent>

            {hasFooter && <DialogActions
                sx={{
                    px : 3,
                    py : 2,
                    gap : 1,
                    display : 'flex',
                    flexDirection : stackActions ? 'column-reverse' : 'row',
                    alignItems : stackActions ? 'stretch' : 'center',
                    justifyContent : 'space-between',
                    '& > :not(style) ~ :not(style)' : { marginLeft: 0 }
                }}
            >
                {/* Cancel anchors the left; everything else groups on the right. */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {showsCancel && <button
                        type="button"
                        className={buttonClass('btn btn-outline-dark')}
                        disabled={closeDisabled || (busy && !noProcessingLoading)}
                        onClick={() => requestClose(cancelAction)}
                    >
                        {cancelText}
                    </button>}
                </Box>

                <Box sx={{
                    display : 'flex',
                    flexDirection : stackActions ? 'column-reverse' : 'row',
                    alignItems : stackActions ? 'stretch' : 'center',
                    gap : 1
                }}>
                    {hasSecondaryActions && secondaryActions.map((button, index) => (
                        <button
                            type="button"
                            key={index}
                            className={buttonClass(
                                button.className
                                || (button.destructive ? 'btn btn-danger mx-1' : 'btn btn-secondary mx-1')
                            )}
                            disabled={button.disabled != null ? button.disabled : busy}
                            onClick={() => {
                                if (button.noAutoDisable) {
                                    button.action();
                                    return;
                                }
                                runAction(index, button.action);
                            }}
                        >
                            {button.loading || processing === index ? spinner : null}
                            {button.text}
                        </button>
                    ))}

                    {secondaryAction != null && <button
                        type="button"
                        className={buttonClass('btn btn-secondary')}
                        disabled={busy || secondaryLoading}
                        onClick={() => runAction('secondary', secondaryAction)}
                    >
                        {secondaryLoading || processing === 'secondary' ? spinner : null}
                        {secondaryText}
                    </button>}

                    {okAction != null && <button
                        type="button"
                        className={buttonClass(destructive ? 'btn btn-danger' : 'btn btn-primary')}
                        // Never hand focus straight to a destructive action.
                        autoFocus={isConfirm && !destructive}
                        disabled={(busy && !noProcessingLoading) || okLoading || invalid}
                        onClick={() => runAction('ok', okAction)}
                    >
                        {okLoading || processing === 'ok' ? spinner : null}
                        {(okLoading || processing === 'ok') && okLoadingText ? okLoadingText : okText}
                    </button>}

                    {okIsSubmit && <button
                        type="submit"
                        className={buttonClass(destructive ? 'btn btn-danger' : 'btn btn-primary')}
                        disabled={busy && !noProcessingLoading}
                    >
                        {showsBusyState ? spinner : null}
                        {showsBusyState && okLoadingText ? okLoadingText : okText}
                    </button>}
                </Box>
            </DialogActions>}
        </Dialog>

        {/* Compact confirmation guarding unsaved edits. */}
        <Dialog
            open={discarding}
            onClose={() => setDiscarding(false)}
            maxWidth={false}
            aria-describedby={`${ids.current.description}-discard`}
            sx={{ zIndex: PROMPT_Z_INDEX }}
            PaperProps={{ sx: { width: SIZES.sm, maxWidth: '100%' } }}
        >
            <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1 }}>
                {translate(data, 'modal_dialog.discard_title', 'Discard changes?')}
            </DialogTitle>
            <DialogContent sx={{ px: 3, py: 1 }}>
                <DialogContentText id={`${ids.current.description}-discard`}>
                    {translate(
                        data,
                        'modal_dialog.discard_message',
                        'Your changes have not been saved. If you close now they will be lost.'
                    )}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <button
                    type="button"
                    className="btn btn-outline-dark"
                    autoFocus
                    onClick={() => setDiscarding(false)}
                >
                    {translate(data, 'modal_dialog.discard_cancel', 'Keep editing')}
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDiscard}>
                    {translate(data, 'modal_dialog.discard_confirm', 'Discard changes')}
                </button>
            </DialogActions>
        </Dialog>
    </>
}));

export default ModalDialog;
